import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { apiRequest } from '@/lib/supabase';
import {
  HOME,
  workspacesFromPermissions,
  defaultWorkspaceFromPermissions,
  personaForPath,
  type PersonaKey,
  type UserRole,
  type RolePermissions,
} from '@/lib/persona';

// Current logged-in user + workspace (persona) access. Reads the real role from
// GET /api/v1/users/me (the role is forced server-side on user_profiles). This is
// what makes RBAC real — previously the client never read the user's role.

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  isAdmin: boolean;
  // Configurable-RBAC permissions blob surfaced by GET /users/me. null =
  // absent/unmigrated → workspace access + canMutate fall back to the hardcoded
  // WORKSPACE_ACCESS + isAdmin model (reversible / net-zero).
  permissions: RolePermissions | null;
}

interface MeResponse {
  id: string;
  email: string;
  profile: {
    role?: UserRole;
    is_admin?: boolean;
    full_name?: string | null;
    permissions?: RolePermissions | null;
  } | null;
}

const LS_KEY = 'vw.workspace';

interface PersonaContextValue {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  workspaces: PersonaKey[];
  canAccess: (p: PersonaKey) => boolean;
  switchPersona: (p: PersonaKey) => void;
  rememberWorkspace: (p: PersonaKey) => void;
  /** Persisted-if-allowed else default workspace — where to land at login / index. */
  landingWorkspace: PersonaKey | null;
}

const PersonaContext = createContext<PersonaContextValue | null>(null);

function readPersisted(): PersonaKey | null {
  try {
    const v = window.localStorage.getItem(LS_KEY);
    return v === 'sales' || v === 'ops' || v === 'audit' ? v : null;
  } catch {
    return null;
  }
}

export function PersonaProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiRequest<MeResponse>('/users/me')
      .then((me) => {
        if (cancelled) return;
        const role = (me.profile?.role ?? 'member') as UserRole;
        setUser({
          id: me.id,
          email: me.email,
          name: me.profile?.full_name || me.email?.split('@')[0] || 'User',
          role,
          isAdmin: !!me.profile?.is_admin,
          permissions: me.profile?.permissions ?? null,
        });
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load profile');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const workspaces = workspacesFromPermissions(user?.permissions, user?.role);
  const canAccess = useCallback((p: PersonaKey) => workspaces.includes(p), [workspaces]);

  const rememberWorkspace = useCallback((p: PersonaKey) => {
    try {
      window.localStorage.setItem(LS_KEY, p);
    } catch {
      /* localStorage may be unavailable; persona still works in-memory */
    }
  }, []);

  const switchPersona = useCallback(
    (p: PersonaKey) => {
      rememberWorkspace(p);
      navigate(HOME[p]);
    },
    [navigate, rememberWorkspace]
  );

  let landingWorkspace: PersonaKey | null = null;
  if (user) {
    const persisted = readPersisted();
    landingWorkspace = persisted && workspaces.includes(persisted)
      ? persisted
      : defaultWorkspaceFromPermissions(user.permissions, user.role);
  }

  return (
    <PersonaContext.Provider
      value={{ user, loading, error, workspaces, canAccess, switchPersona, rememberWorkspace, landingWorkspace }}
    >
      {children}
    </PersonaContext.Provider>
  );
}

export function usePersona(): PersonaContextValue {
  const ctx = useContext(PersonaContext);
  if (!ctx) throw new Error('usePersona must be used within PersonaProvider');
  return ctx;
}

// Non-throwing: reads the context directly and returns null when there is no
// PersonaProvider (e.g. a page rendered bare in a unit test). This lets callers
// invoke the hook UNCONDITIONALLY (satisfying react-hooks/rules-of-hooks) while
// still degrading safely to "no elevated user" outside a provider.
export function useCurrentUser(): CurrentUser | null {
  return useContext(PersonaContext)?.user ?? null;
}

// RBAC for the UI: "may this user perform write actions in the CURRENT
// workspace?" Audit is read-only for everyone, and only admin/super_admin
// pass the backend's requireAdmin — so non-admins must never see write
// controls that would just 403. Shared pages (mounted under /sales, /ops
// AND /audit) read this instead of assuming they're always writable.
export function useCanMutate(): boolean {
  const { user } = usePersona();
  const location = useLocation();
  const persona = personaForPath(location.pathname);
  // Audit is read-only for everyone, always.
  if (persona === 'audit') return false;
  // Data-driven when permissions are present; else fall back to is_admin (today's
  // behavior). Mirrors backend/src/lib/rbac.ts canMutateFor exactly. The server
  // still enforces every mutation via requireAdmin regardless of this convenience.
  if (user?.permissions != null) return user.permissions['mutate'] === true;
  return !!user?.isAdmin;
}
